<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import {
  PhoneIcon,
  UserIcon,
  UserGroupIcon,
  ChatBubbleLeftIcon,
} from "@heroicons/vue/24/solid";
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  TrashIcon,
  PlusIcon,
  PencilIcon,
} from "@heroicons/vue/24/outline";
import type { AiModel, AiRoleCard, DmConversationSummary, DmMessage } from "~/types/entities";
import { resolveErrorMessage } from "~/utils/api-error";
import { formatTime } from "~/utils/time";
import { stripMentionsToPlain } from "~/utils/mention";
import { stripEmotesToPlain } from "~/utils/emote";
import { extractCitations, isWorkflowSettled } from "~/utils/workflow";
import type { BubbleRender, EnrichedMessage } from "~/utils/dm-view";
import type DmComposer from "~/components/DmComposer.vue";
import FairyMascotEye from "~/components/fairy/FairyMascotEye.vue";
import Collapsible from "~/components/ui/collapsible/Collapsible.vue";
import CollapsibleContent from "~/components/ui/collapsible/CollapsibleContent.vue";
import { useChatScroll } from "~/composables/useChatScroll";
import { useDmMessageEntries } from "~/composables/useDmMessageEntries";
import { readKnockTarget, type KnockTab } from "~/utils/overlay-history";
import { openPostLink } from "~/utils/post-link";
import { isAnyGalleryOpen } from "~/composables/useImageGallery";

const {
  visible,
  requestId,
  close,
  consumePendingDmConversationId,
  consumePendingKnockTab,
  updateUrl,
} = useKnockKnockModal();
const auth = useAuthStore();
const postModal = usePostModal();
const loginDialog = useLoginDialog();
const confirmDialog = useConfirmDialog();
const { characters: aiCharacters, loading: aiCharactersLoading, error: aiCharactersError, refresh: refreshAiCharacters } = useAiCharacters();
const { models: aiModels, refresh: refreshAiModels } = useAiModels();
const { usage: agentQuota, refresh: refreshAgentQuota } = useAgentQuota();
const {
  displayText: aiDisplayText,
  startReveal: startAiReveal,
  primeCompleted: primeAiRevealCompleted,
  resetSession: resetAiRevealSession,
  isComplete: isAiRevealComplete,
  isRevealing: isAiRevealing,
} = useAiDmTypewriter();

/** 打开会话时已有的消息 id；不在此集合内的 AI 新消息才打字机 */
const historyBaselineIds = ref(new Set<string>());
const aiRevealSessionReady = ref(false);

/** 顶部 tab：通话 / 私聊 / 群聊（未来占位） */
const activeTab = ref<KnockTab>("contacts");

const {
  conversations: allConversations,
  isLoading,
  error: loadError,
  refresh,
  ensureConversation,
  hasMoreConversations,
  isLoadingMoreConversations,
  loadMoreConversations,
  ensureMessages,
  messageStateOf,
  markConversationAsRead,
  sendMessage,
  editMessage,
  withdrawMessage,
  /** 当前选中的会话 documentId（共享自 composable；null 表示右栏显示 EMPTY 占位） */
  activeConversationId,
  typingByConversation,
  sendTyping,
  startStream,
  createAiSession,
  deleteConversation,
  loadAiSessions,
  aiSessionsHasMore,
  aiSessionsLoading,
  isStreamingMessage,
  stopAiStream,
  regenerateAiReply,
  workflowEventsOf,
  updateConversation,
  clearTyping,
} = useDmConversations();

const AI_SLUG_STORAGE_KEY = "ik-knock-ai-slug";
const activeAiSlug = ref<string | null>(null);

/** 未配置示例问题时，AI 会话使用的默认示例问题 */
const DEFAULT_AI_SUGGESTIONS = [
  "介绍一下你自己",
  "最近有什么热门委托？",
  "帮我找一下最新情报",
  "分析一下当前版本的角色强度",
  "给我推荐一些值得关注的帖子",
];
/** 弹窗打开后 DM 列表 + AI 角色列表均就绪，再渲染私聊 Tab（避免 fairy 闪一下） */
const knockBootstrapDone = ref(false);

const isOfficialAiPeer = (conv: DmConversationSummary): boolean => {
  // AI 发来的通知分组（评论区回复等）不是 AI 会话，和真人的通知分组一样留在私聊 Tab
  if (conv.pseudoKind) return false;
  if (conv.peer?.isAiAgent === true) return true;
  const uid = conv.peer?.userId;
  return typeof uid === "string" && aiPeerUserIds.value.has(uid);
};

/** 当前登录用户的 user.id；用于区分消息气泡是「我发的」还是「对方发的」 */
const selfUserId = computed<string | null>(() => {
  const id = auth.user?.id;
  return id != null && id !== "" ? String(id) : null;
});

/**
 * 弹窗打开时加载内容；关闭只清选中，登录级 WS 继续更新全站未读。
 *
 * 本组件是懒加载的（app.vue 的 LazyKnockKnockModal）：直达 /knock 路径时，
 * open() 可能早于本组件 setup，visible 一出生就是 true，watch 不会触发——
 * 所以 onMounted 里还要补一次（见下方），否则列表永远停在「加载中…」。
 */
let bootstrapRun = 0;
let selectionVersion = 0;
// 用户选择优先于打开弹窗时的目标；列表仍继续加载，旧目标不再写回。
const cancelPendingSelection = () => {
  selectionVersion++;
  consumePendingDmConversationId();
  consumePendingKnockTab();
};
const onVisibleChange = async (next: boolean) => {
  const run = ++bootstrapRun;
  const selection = selectionVersion;
  if (!next) {
    const closingId = activeConversationId.value;
    if (closingId) {
      void markConversationAsRead(closingId, { force: true });
    }
    activeTab.value = "contacts";
    activeAiSlug.value = null;
    activeConversationId.value = null;
    knockBootstrapDone.value = false;
    aiRevealSessionReady.value = false;
    historyBaselineIds.value = new Set();
    return;
  }
  activeTab.value = readKnockTarget(new URL(window.location.href))?.tab ?? "contacts";
  activeAiSlug.value = null;
  activeConversationId.value = null;
  knockBootstrapDone.value = false;
  aiRevealSessionReady.value = false;
  historyBaselineIds.value = new Set();
  autoFillUsed.value = 0;
  sendError.value = null;
  await auth.ensureCredentials();
  if (!visible.value || run !== bootstrapRun) return;
  if (!auth.isLogin) {
    knockBootstrapDone.value = true;
    loginDialog.open();
    return;
  }
  // 拉列表 + 起 WS（startStream 内部对 SSR / 未登录都做了护栏）
  startStream();
  const generation = auth.generation;
  await Promise.all([refresh(), refreshAiCharacters(), refreshAiModels(), refreshAgentQuota()]);
  if (!visible.value || run !== bootstrapRun || auth.generation !== generation) return;
  knockBootstrapDone.value = true;
  if (selection !== selectionVersion) return;
  // 直达链接或个人页「私信」指定的会话可能不在收件箱第一页。
  const pendingDm = consumePendingDmConversationId();
  const pendingTab = consumePendingKnockTab();
  if (pendingTab) activeTab.value = pendingTab;
  if (pendingDm) {
    let conv: DmConversationSummary | null;
    try {
      conv = await ensureConversation(pendingDm);
    } catch (err) {
      if (visible.value && run === bootstrapRun && auth.generation === generation && selection === selectionVersion) {
        sendError.value = resolveErrorMessage(err, "无法打开该会话");
      }
      return;
    }
    if (!visible.value || run !== bootstrapRun || auth.generation !== generation || selection !== selectionVersion) return;
    if (!conv) {
      sendError.value = "会话不存在或当前账号无法访问";
      return;
    }
    const peerUid = conv?.peer?.userId;
    const aiCard =
      typeof peerUid === "string"
        ? aiCharacters.value.find((c) => c.boundUser?.id === peerUid)
        : undefined;
    if (conv && !conv.pseudoKind && (conv.peer?.isAiAgent || aiCard)) {
      activeTab.value = "calls";
      activeAiSlug.value = aiCard?.slug ?? null;
      activeConversationId.value = pendingDm;
      updateUrl("calls", pendingDm);
      if (aiCard) void loadAiSessions(aiCard.slug);
      return;
    }
    activeTab.value = "contacts";
    activeConversationId.value = pendingDm;
    updateUrl("contacts", pendingDm);
    return;
  }
  updateUrl(activeTab.value);
};
watch([visible, requestId], ([next]) => { void onVisibleChange(next); });
watch(() => auth.isLogin, (loggedIn) => {
  if (loggedIn && visible.value) void onVisibleChange(true);
});
onMounted(() => {
  // 挂载前就已经打开（直达链接）：补跑打开流程
  if (visible.value) void onVisibleChange(true);
});

/** 官方 AI 绑定的 userId（私聊 Tab 中隐藏，仅在「通话」展示） */
const aiPeerUserIds = computed(() => {
  const ids = new Set<string>();
  for (const card of aiCharacters.value) {
    const uid = card.boundUser?.id;
    if (typeof uid === "string") ids.add(uid);
  }
  return ids;
});

/** 私聊 Tab 列表是否仍在首屏加载（未就绪时不渲染会话项，防闪烁） */
const contactsListLoading = computed(
  () => !knockBootstrapDone.value || isLoading.value || aiCharactersLoading.value,
);

/**
 * 私聊 Tab：排除与官方 AI 角色的 direct 会话（避免与「通话」重复），
 * 并按（置顶 desc、lastMessageAt desc）重排，确保发消息后实时置顶。
 */
const conversations = computed<DmConversationSummary[]>(() => {
  if (activeTab.value !== "contacts" || contactsListLoading.value) return [];
  return allConversations.value
    .filter((c) => !isOfficialAiPeer(c))
    .sort((a, b) => {
      const ap = a.self?.pinned ? 1 : 0;
      const bp = b.self?.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;
      const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bt - at;
    });
});

const activeConversation = computed<DmConversationSummary | null>(() => {
  if (!activeConversationId.value) return null;
  return allConversations.value.find((c) => c.documentId === activeConversationId.value) ?? null;
});

/* ─────────────── 私聊列表分页（滚到底自动加载） ─────────────── */

/** 可见项少于这个数就主动续拉，保证哨兵有机会进入视口 */
const MIN_VISIBLE_CONVERSATIONS = 12;
/**
 * 「可见项太少」这条路径单次打开弹窗内最多自动续拉几页。
 * 它不需要用户操作就会触发，所以必须封顶，防止「整页都被过滤掉」时无限翻页。
 * 用户真实滚到底（哨兵进入视口）走另一条路径，不受此限制。
 */
const MAX_AUTO_FILL_PAGES = 5;

const autoFillUsed = ref(0);
const contactsListRef = ref<HTMLElement | null>(null);
const contactsSentinelRef = ref<HTMLElement | null>(null);
let contactsObserver: IntersectionObserver | null = null;

const teardownContactsObserver = () => {
  contactsObserver?.disconnect();
  contactsObserver = null;
};

/**
 * 哨兵进入视口即拉下一页。
 *
 * root 取列表容器本身——列表在弹窗内部独立滚动，用 viewport 当 root 永远不会触发。
 * 列表随 tab 切换 v-if 销毁重建，所以观察对象要跟着 ref 变化重新绑定。
 */
watch(contactsSentinelRef, (el) => {
  teardownContactsObserver();
  if (!el || !contactsListRef.value) return;
  contactsObserver = new IntersectionObserver(
    (entries) => {
      // 用户真实滚到底：不设页数上限，滚多少加多少
      if (entries.some((e) => e.isIntersecting)) void loadMoreConversations();
    },
    { root: contactsListRef.value, rootMargin: "120px" },
  );
  contactsObserver.observe(el);
});

/**
 * 过滤穿透保护：私聊 Tab 会把官方 AI 会话过滤掉（它们归「通话」Tab），
 * 一页 20 条可能只剩几条可见，哨兵挤在首屏之内不会触发 intersection。
 * 这里在可见项过少时主动续拉，MAX_AUTO_FILL_PAGES 封顶。
 *
 * isLoadingMoreConversations 进了依赖：整页都被过滤掉时可见数不变，只靠
 * conversations.length 无法驱动下一轮；而哨兵持续停在视口内也不会再产生
 * intersection 回调，续拉链条会在这里断掉。
 */
watch(
  () =>
    [
      activeTab.value,
      conversations.value.length,
      hasMoreConversations.value,
      isLoadingMoreConversations.value,
    ] as const,
  ([tab, visible, hasMore, loadingMore]) => {
    if (tab !== "contacts" || !hasMore || loadingMore) return;
    // 首屏 / 静默 refresh 期间 conversations 被临时清空（contactsListLoading 防闪烁），
    // 那不是「内容不够」，此时续拉纯属白跑一次后端
    if (contactsListLoading.value) return;
    if (visible >= MIN_VISIBLE_CONVERSATIONS) {
      autoFillUsed.value = 0; // 已经填满一屏，配额还给下一次变稀疏的时候
      return;
    }
    if (autoFillUsed.value >= MAX_AUTO_FILL_PAGES) return;
    autoFillUsed.value += 1;
    void loadMoreConversations();
  },
);

/** 当前会话是否为官方 AI 角色（决定是否显示会话管理按钮） */
const isActiveAiConversation = computed<boolean>(() => {
  const conv = activeConversation.value;
  return !!conv && isOfficialAiPeer(conv);
});
/** 私信附图（真人私聊与 AI 会话都能发） */
const dmImages = useDmImages();

/** 当前选中的 AI 角色卡（从 slug 或当前会话反推） */
const activeAiCard = computed<AiRoleCard | null>(() => {
  if (activeAiSlug.value) {
    return aiCharacters.value.find((c) => c.slug === activeAiSlug.value) ?? null;
  }
  const conv = activeConversation.value;
  const uid = conv?.peer?.userId;
  if (typeof uid === "string") {
    return aiCharacters.value.find((c) => c.boundUser?.id === uid) ?? null;
  }
  return null;
});

/** 是否为 Fairy 专属 AI 对话 */
const isFairyConversation = computed(() => {
  if (!isActiveAiConversation.value) return false;
  const slug = (activeAiCard.value?.slug || "").toLowerCase();
  const displayName = (activeAiCard.value?.displayName || activeConversation.value?.peer?.name || "").toLowerCase();
  return slug === "fairy" || slug.includes("fairy") || displayName.includes("fairy") || displayName.includes("斐林");
});

/** Fairy Mascot 动态表情状态：normal 待机 | thinking 思考中 */
const fairyMascotState = computed<"normal" | "thinking">(() => {
  if (activeStreamingMessageId.value || peerIsTyping.value) {
    return "thinking";
  }
  return "normal";
});

/** 当前角色有没有真实的热门问题（有就只展示它们，菜单标题才叫「大家都在问」） */
const activeAiHasPopular = computed(() => (activeAiCard.value?.popularQuestions?.length ?? 0) > 0);

/**
 * AI 示例问题：优先用大家真实问过的热门问题；还没有数据时退回角色卡示例问题，角色卡也没配就用默认那一组。
 * 在输入框里轮播，也能从输入框的灵感菜单里点。
 */
const activeAiSuggestions = computed<string[]>(() => {
  const card = activeAiCard.value;
  if (card?.popularQuestions?.length) return card.popularQuestions;
  return card?.suggestedQuestions?.length
    ? (card.suggestedQuestions as string[])
    : DEFAULT_AI_SUGGESTIONS;
});

/* ─────────────── 模型切换（会话级） ─────────────── */

/**
 * 当前会话可选模型：非 AI 会话返回 undefined（DmModelPicker 据此整体不渲染）。
 * 角色卡 allowedModelKeys 为空表示不限制；白名单过滤后为空则同样隐藏，
 * 避免给出一个点了必被后端拒的列表。
 */
const activeModelOptions = computed<AiModel[] | undefined>(() => {
  if (!isActiveAiConversation.value) return undefined;
  const list = aiModels.value;
  if (list.length === 0) return undefined;
  const allow = activeAiCard.value?.allowedModelKeys;
  if (!allow || allow.length === 0) return list;
  const allowSet = new Set(allow);
  const filtered = list.filter((m) => allowSet.has(m.key));
  return filtered.length > 0 ? filtered : undefined;
});

/** 会话级选择（null = 跟随角色卡默认模型） */
const activeModelKey = computed<string | null>(
  () => activeConversation.value?.aiModelKey ?? null,
);

/**
 * 切换模型：乐观更新由 updateConversation 完成，失败时回落到 sendError 提示
 * （下一条消息仍会用服务端权威值，不会静默用错模型）。
 */
const onSelectModel = async (key: string | null) => {
  const id = activeConversationId.value;
  if (!id || id.startsWith("pseudo:")) return;
  try {
    await updateConversation(id, { aiModelKey: key });
  } catch (e: unknown) {
    sendError.value = resolveErrorMessage(e, "切换模型失败");
  }
};

/**
 * 当前 AI 角色的全部会话，按 lastMessageAt 降序。
 *
 * 必须排除 pseudo 项：AI 角色本身是真实用户账号，它给你的评论/回复也会产生通知，
 * 而通知聚合桶在「与该用户没有活跃 direct 会话」时会以 `pseudo:user:<uid>` 形态
 * 出现在列表里（后端 notification-merge）。删掉最后一个 AI 会话后就正好是这种状态，
 * 若不过滤，这个通知桶会被当成「该角色的会话」自动打开 —— 界面变成一串点赞/回复
 * 通知，而且往里发消息会把它实质化成一条普通私聊。
 */
const aiSessionsForActiveCard = computed<DmConversationSummary[]>(() => {
  const card = activeAiCard.value;
  const uid = card?.boundUser?.id;
  if (typeof uid !== "string") return [];
  return allConversations.value
    .filter((c) => !c.pseudoKind && c.peer?.userId === uid)
    .sort((a, b) => {
      const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bt - at;
    });
});

const creatingAiSession = ref(false);
const deletingSessionId = ref<string | null>(null);

/**
 * 对端个人主页 URL（不可跳转时为 null）。
 * 判断条件（非 AI、有 authorDocumentId）在此唯一维护，
 * canClickPeerProfile 从它派生，避免两处重复判断逻辑不同步。
 */
const peerProfileUrl = computed<string | null>(() => {
  const peer = activeConversation.value?.peer;
  if (!peer?.authorDocumentId || peer.isAiAgent) return null;
  return profilePath(peer.authorDocumentId, peer.memberNo);
});

/** 当前会话对端是否可跳转个人主页——从 peerProfileUrl 派生 */
const canClickPeerProfile = computed<boolean>(() => peerProfileUrl.value !== null);

/** 通话 Tab：按 AI boundUserId 汇总该角色所有会话的未读（pseudo 通知桶不算，见 aiSessionsForActiveCard） */
const aiUnreadByUserId = computed(() => {
  const map = new Map<string, number>();
  for (const c of allConversations.value) {
    if (c.pseudoKind) continue;
    const uid = c.peer?.userId;
    if (typeof uid === "string") {
      map.set(uid, (map.get(uid) ?? 0) + (c.unreadCount ?? 0));
    }
  }
  return map;
});

const aiCharacterRows = computed(() =>
  aiCharacters.value.map((card) => {
    const uid = card.boundUser?.id;
    const unread =
      typeof uid === "string" ? (aiUnreadByUserId.value.get(uid) ?? 0) : 0;
    return { card, unread };
  }),
);

const cardAvatarUrl = (card: AiRoleCard): string | null => {
  const raw = card.avatar || card.boundUser?.avatar;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
};

/** 选择 AI 角色：展开它的会话列表，打开最近一条；一条都没有就新建 */
const selectAiCharacter = async (card: AiRoleCard) => {
  const uid = card.boundUser?.id;
  if (!uid) return;
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  cancelPendingSelection();
  activeAiSlug.value = card.slug;
  if (import.meta.client) {
    localStorage.setItem(AI_SLUG_STORAGE_KEY, card.slug);
  }
  // 收件箱按页加载，较早的会话可能还没拿到；先按角色拉一遍再决定打开哪条
  await loadAiSessions(card.slug);
  if (activeAiSlug.value !== card.slug) return;
  const first = aiSessionsForActiveCard.value[0];
  if (first) {
    openAiSession(first.documentId);
  } else {
    await createNewAiSession();
  }
};

/** 打开当前角色下的某条会话 */
const openAiSession = (id: string) => {
  cancelPendingSelection();
  if (renamingSessionId.value && renamingSessionId.value !== id) void commitRename();
  activeConversationId.value = id;
  updateUrl("calls", id);
};

/** 为当前 AI 角色新建一个独立会话 */
const createNewAiSession = async () => {
  const card = activeAiCard.value;
  const uid = card?.boundUser?.id;
  if (!uid || creatingAiSession.value) return;
  cancelPendingSelection();
  creatingAiSession.value = true;
  try {
    const summary = await createAiSession(card.slug);
    if (card) activeAiSlug.value = card.slug;
    openAiSession(summary.documentId);
  } catch (e: unknown) {
    sendError.value = resolveErrorMessage(e, "新建对话失败");
  } finally {
    creatingAiSession.value = false;
  }
};

/** 「新对话」：已有还没聊过的空会话就直接用它，避免攒一堆空会话 */
const startNewAiSession = async () => {
  const empty = aiSessionsForActiveCard.value.find((s) => !s.lastMessage);
  if (empty) {
    openAiSession(empty.documentId);
    return;
  }
  await createNewAiSession();
};

/** 会话列表里显示的名字：标题 → 最后一条消息 → 「新对话」 */
const aiSessionLabel = (s: DmConversationSummary): string =>
  s.title?.trim() || conversationPreview(s) || "新对话";

const renamingSessionId = ref<string | null>(null);
const renameDraft = ref("");
const renameInputRef = ref<HTMLInputElement | HTMLInputElement[] | null>(null);

const startRename = async (s: DmConversationSummary) => {
  renamingSessionId.value = s.documentId;
  renameDraft.value = s.title?.trim() || "";
  await nextTick();
  const el = Array.isArray(renameInputRef.value) ? renameInputRef.value[0] : renameInputRef.value;
  el?.focus();
  el?.select();
};

const cancelRename = () => {
  renamingSessionId.value = null;
};

const commitRename = async () => {
  const id = renamingSessionId.value;
  if (!id) return;
  renamingSessionId.value = null;
  const title = renameDraft.value.trim().slice(0, 64);
  const conv = allConversations.value.find((c) => c.documentId === id);
  if ((conv?.title ?? "") === title) return;
  try {
    await updateConversation(id, { title });
  } catch (e: unknown) {
    sendError.value = resolveErrorMessage(e, "改名失败");
  }
};

/** 删除指定 AI 会话；删的是正在看的那条，就切到同角色的下一条 */
const deleteAiSession = async (id: string) => {
  if (!id || deletingSessionId.value === id) return;
  const ok = await confirmDialog.open({
    title: "删除对话",
    message: "删除后聊天记录不再显示。",
    confirmText: "删除",
    danger: true,
  });
  if (!ok) return;
  deletingSessionId.value = id;
  try {
    await deleteConversation(id);
    if (activeConversationId.value === id) {
      const next = aiSessionsForActiveCard.value.find((s) => s.documentId !== id);
      if (next) {
        openAiSession(next.documentId);
      } else {
        activeConversationId.value = null;
        updateUrl("calls");
      }
    }
  } catch (e: unknown) {
    sendError.value = resolveErrorMessage(e, "删除失败");
  } finally {
    deletingSessionId.value = null;
  }
};

/**
 * 当前激活会话是否禁止发送消息：
 *  - pseudo:anonymous（匿名通知）：对端没有真实身份，无法回复
 *  - pseudo:system（系统通知）：单向通知，不可回复
 *  - pseudo:user：可发，发出去的瞬间会 lazy 实质化为真 DM（见 useDmConversations.sendMessage）；
 *    AI 发来的通知分组除外：和 AI 聊天走「通话」Tab，这里发出去只会建出一条不会被回复的普通私聊
 */
const isAiNotificationBucket = (conv: DmConversationSummary): boolean =>
  conv.pseudoKind === "user" &&
  (conv.peer?.isAiAgent === true ||
    (typeof conv.peer?.userId === "string" && aiPeerUserIds.value.has(conv.peer.userId)));

const composerDisabled = computed<boolean>(() => {
  const conv = activeConversation.value;
  if (!conv) return true;
  return conv.pseudoKind === "anonymous" || conv.pseudoKind === "system" || isAiNotificationBucket(conv);
});

const headerQuota = computed(() =>
  activeConversation.value && !composerDisabled.value && isActiveAiConversation.value
    ? agentQuota.value
    : null,
);
const hasHeaderTools = computed(() => !!activeModelOptions.value?.length || !!headerQuota.value);

/** 对端是否正在输入：当前会话的 typing 用户列表非空且不含自己 */
const peerIsTyping = computed<boolean>(() => {
  const cid = activeConversationId.value;
  if (!cid) return false;
  const list = typingByConversation.value[cid];
  if (!list || list.length === 0) return false;
  const self = selfUserId.value;
  return list.some((uid) => uid !== self);
});

/** 节流发送 typing 状态：2s 内最多触发一次（DmComposer @typing 触发） */
let typingThrottleLast = 0;
let typingThrottleTimer: ReturnType<typeof setTimeout> | null = null;
const handleComposerTyping = () => {
  const cid = activeConversationId.value;
  if (!cid) return;
  const now = Date.now();
  const remaining = 2000 - (now - typingThrottleLast);
  if (remaining <= 0) {
    typingThrottleLast = now;
    sendTyping(cid);
  } else if (!typingThrottleTimer) {
    typingThrottleTimer = setTimeout(() => {
      typingThrottleTimer = null;
      const currentCid = activeConversationId.value;
      if (currentCid) {
        typingThrottleLast = Date.now();
        sendTyping(currentCid);
      }
    }, remaining);
  }
};

/** 输入框 placeholder：根据 pseudoKind 给出更精确的提示 */
/** 输入框轮播占位：AI 会话轮播示例问题，普通私聊轮播操作提示；通知分组不轮播 */
const composerRotatingPlaceholders = computed<string[] | undefined>(() => {
  const conv = activeConversation.value;
  if (!conv || conv.pseudoKind) return undefined;
  if (isActiveAiConversation.value) return ["问点什么吧，Enter 发送", ...activeAiSuggestions.value];
  return ["输入消息，Enter 发送", "Shift+Enter 换行", "粘贴截图可以直接发图"];
});

const composerPlaceholder = computed<string>(() => {
  const conv = activeConversation.value;
  if (!conv) return "";
  if (conv.pseudoKind === "anonymous") return "匿名用户的通知不可回复";
  if (conv.pseudoKind === "system") return conv.peer?.name ? `${conv.peer.name} 不可回复` : "系统通知不可回复";
  if (conv.pseudoKind === "user") return "发送将开启与该用户的私聊";
  return "输入消息，Enter 发送，Shift+Enter 换行";
});

/**
 * 当前会话的消息状态——一次 computed 复用给下面 activeMessages /
 * activeMessageLoading，避免多次访问 messageStateOf 工厂在响应式上下文
 * 反复创建对象。
 */
const activeMessageState = computed(() => {
  const id = activeConversationId.value;
  if (!id) return null;
  return messageStateOf(id);
});

/**
 * 当前会话的消息流（createdAt asc）。消息由 ensureMessages(id) 懒加载到
 * composable 内部缓存；WS 事件到达时按 documentId 去重合并。
 */
const activeMessages = computed<DmMessage[]>(
  () => activeMessageState.value?.items ?? [],
);

/** 右栏 loading 占位用：当前会话首次加载消息中且本地尚无缓存 */
const activeMessageLoading = computed<boolean>(() => {
  const s = activeMessageState.value;
  return !!s && s.loading && !s.hydrated;
});

/** 单条消息是否是我自己发的（通知类永远不是「我自己发的」，因此一直靠左） */
const isMine = (msg: DmMessage): boolean => {
  if (msg.kind === "notification") return false;
  const uid = selfUserId.value;
  return uid != null && msg.sender?.userId === uid;
};

/**
 * 气泡正文如何渲染：
 * - 通知 + comment/reply/mention + 有评论原文 → 走 CommentBody，做 @mention 高亮
 * - 其它一律 plain string（普通 DM 消息正文 / 通知预渲染文案）
 *
 * 返回值约定：
 *  - 字符串：直接 {{ }} 出
 *  - { mode: "rich", content } → 走 <CommentBody>
 *（类型定义随 Phase 4 拆分移至 ~/utils/dm-view.ts）
 */
const bubbleText = (msg: DmMessage): BubbleRender => {
  if (msg.deletedAt) return "消息已撤回";
  if (msg.kind === "notification") {
    const k = msg.notificationKind;
    // 评论 / 回复 / @提到：评论原文里可能含 @[name](id) token，让 CommentBody 渲染高亮
    if ((k === "comment" || k === "reply" || k === "mention") && msg.comment?.content) {
      return { mode: "rich", content: msg.comment.content };
    }
    if ((k === "comment" || k === "reply") && !msg.comment?.content) {
      return k === "reply" ? "回复了你的评论 [图片]" : "评论了你的帖子 [图片]";
    }
    // 互动类（like / favorite / denny / system）→ 走后端预渲染的 plain content
  }
  return msg.content ?? "";
};

/** 与官方 AI 私聊（通话 Tab 绑定的 fairy 等） */
const isAiPeerConversation = computed(() => {
  const conv = activeConversation.value;
  return !!conv && isOfficialAiPeer(conv);
});

const shouldAnimateAiMessage = (msg: DmMessage): boolean => {
  if (!aiRevealSessionReady.value || !isAiPeerConversation.value) return false;
  if (isMine(msg) || msg.kind !== "text" || msg.deletedAt) return false;
  return !historyBaselineIds.value.has(msg.documentId);
};

const tryRevealNewAiMessages = () => {
  if (!aiRevealSessionReady.value || !isAiPeerConversation.value) return;
  // 基线未建立时勿扫描（消息已加载但 baseline 尚未写入会误伤历史）
  if (historyBaselineIds.value.size === 0) return;
  for (const msg of activeMessages.value) {
    if (historyBaselineIds.value.has(msg.documentId)) continue;
    if (isMine(msg) || msg.kind !== "text" || msg.deletedAt) continue;
    if (isAiRevealComplete(msg.documentId)) continue;
    // 流式消息（3.2.3）也走放字：增量按批到达，由放字匀速释放，结束后放完剩下的字
    startAiReveal(msg.documentId, msg.content ?? "", isStreamingMessage(msg.documentId));
  }
};

const bubbleTextForDisplay = (msg: DmMessage): BubbleRender => {
  const base = bubbleText(msg);
  if (typeof base !== "string") return base;
  // 打开会话时的历史消息：永远全文（不受打字机 state 影响）
  if (historyBaselineIds.value.has(msg.documentId)) return base;
  return aiDisplayText(msg.documentId, base, shouldAnimateAiMessage(msg));
};

/** 流式占位气泡：消息仍在流式中且内容尚为空（首个 delta 未到）→ 显示"正在输入"加载点 */
const isPendingStreamBubble = (msg: DmMessage): boolean =>
  isStreamingMessage(msg.documentId) && !(msg.content && msg.content.trim().length > 0);

// AI 回复内链接解析（hasBubbleLinks / parseBubbleSegments）已随
// Phase 4 拆分移至 ~/utils/dm-view.ts，由 DmMessageItem 直接消费。

const handleBubbleLink = (_href: string, event: MouseEvent) => {
  openPostLink(event, postModal.open);
};

/** AiMessageBody 内点击 /post/xxx 链接 → 打开委托弹窗 */
const openPostFromBubble = (documentId: string, commentId?: string) => {
  postModal.open(documentId, { commentId });
};

/** like-on-comment：通知关联委托+评论时，quote 卡引用「评论原文」而不是委托标题 */
const isLikeOnComment = (msg: DmMessage): boolean =>
  msg.notificationKind === "like" && !!msg.comment;

/** quote 卡左侧 label */
const quoteLabel = (msg: DmMessage): string => {
  if (isLikeOnComment(msg)) return "评论";
  if (msg.notificationKind === "accepted") return "评论";
  if (msg.notificationKind === "like" || msg.notificationKind === "favorite" || msg.notificationKind === "denny") return "委托";
  if (msg.notificationKind === "system") return "委托";
  return "评论委托"; // comment / reply / mention：引用所在委托
};

/** quote 卡右侧主标题：like-on-comment 引用评论原文，其余引用委托标题 */
const quoteTitle = (msg: DmMessage): string => {
  // quote 卡是纯文本单行预览：mention/emote token 降级为可读文案
  if (isLikeOnComment(msg)) {
    return stripEmotesToPlain(stripMentionsToPlain(msg.comment?.content ?? ""));
  }
  return msg.article?.title ?? "";
};

/** quote 卡是否应该展示：comment/reply/mention 的主体已是评论原文，不再放卡 */
const shouldShowQuote = (msg: DmMessage): boolean => {
  if (msg.kind !== "notification") return false;
  // 有 article 引用就有卡片可点
  if (!msg.article && !msg.comment) return false;
  // comment/reply/mention：主气泡已是评论正文，再放 quote 委托卡
  // like / favorite / like-on-comment 都需要卡
  return true;
};

const goPost = (msg: DmMessage) => {
  if (!msg.article?.documentId) return;
  postModal.open(msg.article.documentId, {
    coverAspectRatio: msg.article.coverAspectRatio ?? undefined,
    preview: { title: msg.article.title },
    commentId: msg.comment?.documentId,
  });
};

/**
 * 跳转个人主页。
 * 保留当前敲敲历史项，返回时恢复原会话。
 * navigateTo 触发路由变化 → app.vue 的 router.beforeResolve 守卫自动
 * 调用 knockModal.teardown() 关闭弹窗，无需手动 close()。
 */
const goToProfile = (profileUrl: string | null) => {
  if (!profileUrl) return;
  void navigateTo(profileUrl);
};

/** 列表预览文案：撤回 / 图片 / 系统 / 通知 / 正常 */
const conversationPreview = (conv: DmConversationSummary): string => {
  const last = conv.lastMessage;
  if (!last) return "";
  if (last.kind === "image") return "[图片]";
  if (last.kind === "system") return last.content || "";
  // notification 走后端预渲染的 content（"赞了你的评论" / 评论正文 等）
  return last.content || "";
};

const messagesRef = ref<HTMLElement | null>(null);
const messagesContentRef = ref<HTMLElement | null>(null);
const {
  showBackToBottom,
  scrollToBottom,
  stopFollowing,
  reset: resetScroll,
} = useChatScroll(messagesRef, messagesContentRef);

/**
 * 切换会话时的"沉降态"：在 DOM 渲染完成 → scrollTop 校正到底部之间，
 * 容器先 visibility: hidden，校正完成后才 visible。
 * 避免长消息流刚渲染时 paint 在顶部（默认 scrollTop=0），紧接着 rAF
 * 才滚到底部造成的"先看见最早消息一闪 → 瞬间跳底"抖动。
 */
const messagesSettling = ref(false);

/** 快照当前批量加载的消息 ID，仅增量到达的新消息才播入场动画 */
const knownMessageIds = ref(new Set<string>());

/** 时间戳显隐：只在“首条”或与前一条间隔 > 5 分钟时展示，避免逐条时间戳过于嘈杂 */
const TIME_GAP_MS = 5 * 60 * 1000;
const shouldShowTime = (msg: DmMessage, previousCreatedAt: string | undefined): boolean => {
  if (previousCreatedAt == null) return true;
  const dCurr = new Date(msg.createdAt).getTime();
  const dPrev = new Date(previousCreatedAt).getTime();
  if (Number.isNaN(dCurr) || Number.isNaN(dPrev)) return false;
  return dCurr - dPrev > TIME_GAP_MS;
};

/**
 * 消息头像是否可点击跳转个人主页：
 * - 有 sender 且 sender 有 authorDocumentId（匿名通知 / 系统消息无）
 * - 非 AI 代理（AI 角色无个人主页）
 */
const canClickAvatar = (msg: DmMessage): boolean => {
  if (!msg.sender) return false;
  if (!msg.sender.authorDocumentId) return false;
  if (msg.sender.isAiAgent) return false;
  return true;
};

const enrichedMessages = useDmMessageEntries(activeMessages, (msg, previousCreatedAt) => {
  const avatarClickable = canClickAvatar(msg);
  const mine = isMine(msg);
  const rendered = bubbleTextForDisplay(msg);
  const aiRich =
    isAiPeerConversation.value &&
    !mine &&
    msg.kind === "text" &&
    !msg.deletedAt &&
    typeof rendered === "string";
  // 工作流事件：定稿后消息自带落库版本（权威）；流式期间读实时推送缓存
  const workflowEvents = aiRich
    ? (msg.workflow?.length ? msg.workflow : workflowEventsOf(msg.documentId))
    : [];
  return {
    msg,
    isMine: mine,
    isNew: !knownMessageIds.value.has(msg.documentId),
    showTime: shouldShowTime(msg, previousCreatedAt),
    rendered,
    aiRich,
    aiStreaming:
      aiRich &&
      (isStreamingMessage(msg.documentId) ||
        (shouldAnimateAiMessage(msg) && isAiRevealing(msg.documentId))),
    pendingStream: isPendingStreamBubble(msg),
    workflowEvents,
    citations:
      workflowEvents.length > 0 && isWorkflowSettled(workflowEvents)
        ? extractCitations(workflowEvents)
        : [],
    copyable: canCopyMessage(msg),
    quote: shouldShowQuote(msg) && msg.article
      ? {
          label: quoteLabel(msg),
          title: quoteTitle(msg),
          article: msg.article,
        }
      : null,
    avatarClickable,
    // avatarClickable 为 true 时 canClickAvatar 已保证 authorDocumentId 存在
    profileUrl: avatarClickable
      ? profilePath(msg.sender!.authorDocumentId, msg.sender!.memberNo)
      : null,
  };
});

/**
 * Phase 4 长会话性能：渲染窗口化。
 * 只渲染最近 renderWindow 条（含最新消息，scrollToBottom / 流式跟随不受影响），
 * 滚动到顶部附近时扩窗加载更早的消息。相比 content-visibility: auto，
 * 窗口化不会裁剪气泡操作条（absolute top:-12px 溢出）也无 findPage 复杂度。
 */
const RENDER_WINDOW_STEP = 80;
const renderWindow = ref(RENDER_WINDOW_STEP);

const visibleMessages = computed<EnrichedMessage[]>(() => {
  const list = enrichedMessages.value;
  if (list.length <= renderWindow.value) return list;
  return list.slice(list.length - renderWindow.value);
});

/** 窗口上方还有未渲染的更早消息（顶部提示条显隐） */
const hasHiddenAbove = computed(
  () => enrichedMessages.value.length > renderWindow.value,
);

/** 扩窗保留原消息的位置；底部同时吐字不应影响这个补偿量。 */
const expandRenderWindow = () => {
  const el = messagesRef.value;
  if (!el) return;
  stopFollowing();
  const anchor = el.querySelector<HTMLElement>("[data-mid]");
  const anchorTop = anchor?.getBoundingClientRect().top;
  renderWindow.value += RENDER_WINDOW_STEP;
  nextTick(() => {
    if (messagesRef.value !== el || !anchor?.isConnected || anchorTop == null) return;
    el.scrollTop += anchor.getBoundingClientRect().top - anchorTop;
  });
};

/** 5 分钟内自己发的、未撤回的文本消息可以编辑/撤回 */
const EDIT_WINDOW_MS = 5 * 60 * 1000;
const canModifyMessage = (msg: DmMessage): boolean => {
  if (!isMine(msg)) return false;
  if (msg.deletedAt) return false;
  if (msg.kind !== "text") return false;
  const ageMs = Date.now() - new Date(msg.createdAt).getTime();
  return ageMs < EDIT_WINDOW_MS;
};

/** 复制（1.4）：任何未撤回的非空文本消息（AI 消息复制的是原始 markdown） */
const canCopyMessage = (msg: DmMessage): boolean =>
  msg.kind === "text" && !msg.deletedAt && !!(msg.content ?? "").trim();

/** 选中会话时：懒加载消息 → 批量 mark-read → 滚到最新消息 */
watch(activeConversationId, async (id, oldId, onCleanup) => {
  let cancelled = false;
  onCleanup(() => { cancelled = true; });
  resetScroll();
  // 附图跟着会话走：切会话就丢弃
  if (id !== oldId) dmImages.clear();
  if (oldId) {
    clearTyping(oldId);
  }
  aiRevealSessionReady.value = false;
  historyBaselineIds.value = new Set();
  resetAiRevealSession();
  // 切换会话时关闭编辑态
  editingMessageId.value = null;
  editingDraft.value = "";
  // 切换会话时复位渲染窗口
  renderWindow.value = RENDER_WINDOW_STEP;
  messagesSettling.value = !!id;
  if (!id) return;
  try {
    await ensureMessages(id);
  } catch (err) {
    if (!cancelled) sendError.value = resolveErrorMessage(err, "加载消息失败");
  }
  await nextTick();
  if (cancelled) return;
  const historyIds = activeMessages.value
    .map((m) => m.documentId)
    .filter((docId): docId is string => typeof docId === "string" && docId.length > 0);
  historyBaselineIds.value = new Set(historyIds);
  knownMessageIds.value = new Set(historyIds);
  if (isAiPeerConversation.value) {
    primeAiRevealCompleted(historyIds);
  }
  await nextTick();
  if (cancelled) return;
  aiRevealSessionReady.value = true;
  void markConversationAsRead(id, { force: true });
  scrollToBottom("instant");
  messagesSettling.value = false;
});

const onMessagesScroll = () => {
  const el = messagesRef.value;
  if (el && el.scrollTop < 120 && hasHiddenAbove.value) {
    expandRenderWindow();
  }
};

/** 补建历史基线：会话 watch 结束时若消息尚未写入缓存，会导致 baseline 为空 + 全员白框 */
const ensureHistoryBaselineIfNeeded = () => {
  if (!aiRevealSessionReady.value || historyBaselineIds.value.size > 0) return;
  const ids = activeMessages.value
    .map((m) => m.documentId)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  if (ids.length === 0) return;
  historyBaselineIds.value = new Set(ids);
  knownMessageIds.value = new Set(ids);
  if (isAiPeerConversation.value) {
    primeAiRevealCompleted(ids);
  }
};

/** 消息条数变化：补基线 / 新 AI 消息打字机 */
watch(
  () => activeMessages.value.length,
  (nextLen, prevLen) => {
    if (!aiRevealSessionReady.value) return;
    ensureHistoryBaselineIfNeeded();
    if (nextLen <= (prevLen ?? 0)) return;
    tryRevealNewAiMessages();
  },
);

/**
 * 新 AI 消息的正文增长（message.delta）或流式结束：把最新累计文本交给放字。
 * 只看不在历史基线里的消息，签名里带长度和流式状态，增量一到就触发。
 */
watch(
  () => {
    if (!aiRevealSessionReady.value || !isAiPeerConversation.value) return "";
    const baseline = historyBaselineIds.value;
    return activeMessages.value
      .filter((m) => !baseline.has(m.documentId) && !isMine(m))
      .map((m) => `${m.documentId}:${m.content?.length ?? 0}:${isStreamingMessage(m.documentId) ? 1 : 0}`)
      .join(",");
  },
  (signature) => {
    if (signature) tryRevealNewAiMessages();
  },
);

const handleBackToBottom = () => scrollToBottom();

// ── 2.1 停止生成 ───────────────────────────────────────
/**
 * 当前会话正在流式接收中的 AI 占位消息 documentId。
 * 只认 isStreamingMessage（worker 仍在生成）；本地打字机动画不算——
 * 那时内容已定稿，停止 worker 没有意义。
 */
const activeStreamingMessageId = computed<string | null>(() => {
  for (const e of enrichedMessages.value) {
    if (e.aiRich && isStreamingMessage(e.msg.documentId)) return e.msg.documentId;
  }
  return null;
});

/** 已发出停止请求（防重复点击）；流式结束（streamingDone）后自动复位 */
const stoppingAi = ref(false);

const handleStopAi = async () => {
  const id = activeStreamingMessageId.value;
  if (!id || stoppingAi.value) return;
  stoppingAi.value = true;
  try {
    await stopAiStream(id);
  } catch {
    stoppingAi.value = false; // 失败允许重试
  }
};

watch(activeStreamingMessageId, (v) => {
  if (!v) {
    stoppingAi.value = false;
    const cid = activeConversationId.value;
    if (cid) {
      clearTyping(cid);
    }
    // token 是回复结束后才扣的，所以定稿这一刻再拉一次额度
    void refreshAgentQuota();
  }
});

// ── 2.2 重新生成 ───────────────────────────────────────
/** 会话最后一条 AI 回复的 documentId：只有它的操作条出现「重新生成」 */
const lastAiMessageId = computed<string | null>(() => {
  const list = enrichedMessages.value;
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i]!;
    if (e.aiRich) return e.msg.documentId;
  }
  return null;
});

const regeneratingAi = ref(false);

const handleRegenerate = async (msg: DmMessage) => {
  if (regeneratingAi.value) return;
  scrollToBottom();
  regeneratingAi.value = true;
  try {
    await regenerateAiReply(msg.documentId);
    // 旧气泡消失（message.deleted）与新占位（message.created）都走 WS 事件
  } catch {
    // 静默失败：用户可重试
  } finally {
    regeneratingAi.value = false;
  }
};

// （bubbleBody 已被 bubbleText 取代——见上方，支持富文本 mention 渲染）

// ── 输入 / 发送 / 编辑 / 撤回 ───────────────────────────
const draft = ref("");
const sending = ref(false);
const sendError = ref<string | null>(null);
/** DmComposer 实例（Phase 4 拆分）：自动增高/字数提示/Enter 发送已内聚到子组件 */
const composerRef = ref<InstanceType<typeof DmComposer> | null>(null);

/** 当前正在编辑的消息 documentId（null 表示无）；编辑时输入框临时改为修改模式 */
const editingMessageId = ref<string | null>(null);
const editingDraft = ref("");

/** 当前消息上下文菜单：右键/长按 触发 */
const contextMenuMessageId = ref<string | null>(null);
const contextMenuStyle = ref<Record<string, string>>({});
const showContextMenu = (e: MouseEvent, msg: DmMessage) => {
  // 可复制或可编辑/撤回其一即可弹出（1.4：所有文本消息支持复制）
  if (!canCopyMessage(msg) && !canModifyMessage(msg)) return;
  e.preventDefault();
  contextMenuMessageId.value = msg.documentId;
  // 菜单贴近鼠标位置；屏幕边缘 clamp
  const x = Math.min(e.clientX, window.innerWidth - 160);
  const y = Math.min(e.clientY, window.innerHeight - 140);
  contextMenuStyle.value = {
    left: `${x}px`,
    top: `${y}px`,
  };
};
const hideContextMenu = () => {
  contextMenuMessageId.value = null;
};

/** 上下文菜单对应的消息与可用操作（决定菜单项显隐） */
const contextMenuMessage = computed(() => {
  const id = contextMenuMessageId.value;
  if (!id) return null;
  return activeMessages.value.find((m) => m.documentId === id) ?? null;
});
const contextMenuCanCopy = computed(
  () => !!contextMenuMessage.value && canCopyMessage(contextMenuMessage.value),
);
const contextMenuCanModify = computed(
  () => !!contextMenuMessage.value && canModifyMessage(contextMenuMessage.value),
);

/** 1.4 复制消息原文到剪贴板（AI 消息即原始 markdown）；气泡操作条 + 上下文菜单共用 */
const copiedMessageId = ref<string | null>(null);
let copiedFlashTimer: ReturnType<typeof setTimeout> | null = null;
const copyMessageText = async (msg: DmMessage) => {
  hideContextMenu();
  const text = (msg.content ?? "").trim();
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copiedMessageId.value = msg.documentId;
    if (copiedFlashTimer) clearTimeout(copiedFlashTimer);
    copiedFlashTimer = setTimeout(() => {
      copiedMessageId.value = null;
    }, 2000);
  } catch {
    // 剪贴板权限被拒（非 https / 权限策略）：静默失败，浏览器自身会提示
  }
};

const beginEdit = (msg: DmMessage) => {
  editingMessageId.value = msg.documentId;
  editingDraft.value = msg.content ?? "";
  hideContextMenu();
  nextTick(() => {
    composerRef.value?.focus();
  });
};

const cancelEdit = () => {
  editingMessageId.value = null;
  editingDraft.value = "";
};

const doWithdraw = async (msg: DmMessage) => {
  const cid = activeConversationId.value;
  if (!cid) return;
  hideContextMenu();
  try {
    await withdrawMessage(cid, msg.documentId);
  } catch (err) {
    sendError.value = resolveErrorMessage(err, "撤回失败");
  }
};

/** 上下文菜单操作的统一入口：根据当前 contextMenuMessageId 查到消息再分发 */
const onContextMenuAction = (action: "copy" | "edit" | "withdraw") => {
  const id = contextMenuMessageId.value;
  if (!id) return;
  const msg = activeMessages.value.find((m) => m.documentId === id);
  if (!msg) {
    hideContextMenu();
    return;
  }
  if (action === "copy") void copyMessageText(msg);
  else if (action === "edit") beginEdit(msg);
  else void doWithdraw(msg);
};

const doSend = async () => {
  if (sending.value) return;
  // 一次性快照所有响应式 ref——await 期间用户可能切会话 / 退出编辑态，
  // 直接读 .value 会拿到 stale 数据，把消息发到错误的会话里。
  const cid = activeConversationId.value;
  if (!cid) return;
  const editingId = editingMessageId.value;
  const newContent = (editingId ? editingDraft.value : draft.value).trim();
  const imageIds = editingId ? [] : [...dmImages.imageIds.value];
  if (!editingId && dmImages.uploading.value) return;
  if (!newContent && !imageIds.length) return;

  if (!editingId) scrollToBottom();
  sending.value = true;
  sendError.value = null;
  try {
    if (editingId) {
      await editMessage(cid, editingId, newContent);
      // 仅当用户还在原编辑态时才清理，避免 race 时把别人的编辑态清掉
      if (editingMessageId.value === editingId) cancelEdit();
    } else {
      await sendMessage(cid, { content: newContent, imageIds });
      // 同样：仅当用户还在原会话时才清 draft
      if (activeConversationId.value === cid) {
        draft.value = "";
        dmImages.clear();
      }
    }
  } catch (err) {
    sendError.value = resolveErrorMessage(err, editingId ? "编辑失败" : "发送失败");
  } finally {
    sending.value = false;
  }
};

// Enter 发送 / Shift+Enter 换行的键盘处理已随 Phase 4 拆分内聚到 DmComposer

/** ESC 优先关闭：上下文菜单 → 编辑模式 → 弹窗本体 */
const onKeyDown = (e: KeyboardEvent) => {
  if (e.key !== "Escape" || e.defaultPrevented || e.repeat || !visible.value || isAnyGalleryOpen.value) return;
  // 如果有更上层的弹窗（如委托详情 overlay）处于打开状态，不关闭敲敲
  // 敲敲自身也是 .ik-overlay，所以检查数量 > 1
  if (document.querySelectorAll(".ik-overlay").length > 1) return;
  // 图片弹窗自己处理 ESC
  if (dmImages.showPicker.value) return;
  e.preventDefault();
  if (contextMenuMessageId.value) {
    hideContextMenu();
    return;
  }
  if (editingMessageId.value) {
    cancelEdit();
    return;
  }
  close();
};

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
  teardownContactsObserver();
  if (copiedFlashTimer) clearTimeout(copiedFlashTimer);
});

const handleClose = () => {
  close();
};


const handleBackdropMouseDown = (e: MouseEvent) => {
  if (e.target === e.currentTarget) handleClose();
};

const handleTabClick = (tab: KnockTab) => {
  cancelPendingSelection();
  activeTab.value = tab;
  activeConversationId.value = null;
  activeAiSlug.value = null;
  updateUrl(tab);
};

const handleConversationClick = (id: string) => {
  cancelPendingSelection();
  activeConversationId.value = id;
  updateUrl(activeTab.value, id);
};

/** 移动端是否处于「聊天」视图（选中了会话）；用于全屏单栏切换 list ↔ chat */
const mobileChatOpen = computed(() => !!activeConversationId.value);

/** 移动端聊天页返回：清空选中回到会话列表（不关闭弹窗） */
const handleMobileBack = () => {
  cancelPendingSelection();
  const id = activeConversationId.value;
  if (id) void markConversationAsRead(id, { force: true });
  activeConversationId.value = null;
  // 通话 Tab 返回时保留展开的角色，方便切换或新建对话
  if (activeTab.value !== "calls") activeAiSlug.value = null;
  updateUrl(activeTab.value);
};

</script>

<template>
  <Teleport to="body">
    <!-- appear：app.vue 在首次打开时才挂载本组件，首次打开也要播放入场动画 -->
    <Transition name="ik-overlay" appear>
      <div
        v-if="visible"
        class="ik-overlay"
        @mousedown.self="handleBackdropMouseDown"
      >
        <!-- 斜线纹理背景（与委托弹窗一致） -->
        <div class="ik-overlay__stripe" aria-hidden="true"></div>

        <div
          class="ik-dialog ik-dialog--knock"
          :class="{ 'is-mobile-chat': mobileChatOpen }"
          @click.stop
        >
          <!-- 外边框（半透明白色，三圆角） -->
          <div class="ik-dialog__outer">
            <!-- 内边框（纯黑，三圆角） -->
            <div class="ik-dialog__inner">
              <!-- Header Bar -->
              <div class="ik-dialog__header ik-knock__header">
                <div class="ik-knock__brand">
                  <span class="ik-knock__brand-icon" aria-hidden="true">
                    <!-- 自绘：歪着的黄色手机 + 屏幕上的爱心 + 信号波，照游戏内敲敲的 logo -->
                    <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
                      <g class="ik-knock__brand-phone">
                        <rect x="8" y="10" width="27" height="35" rx="6.5" fill="#fbfe00" stroke="#000" stroke-width="3.5" />
                        <rect x="12" y="14.5" width="19" height="22" rx="3" fill="#000" />
                        <path
                          d="M21.5 32.5 15.2 26.4a3.9 3.9 0 0 1 6.3-4.6 3.9 3.9 0 0 1 6.3 4.6z"
                          fill="#fbfe00"
                        />
                        <rect x="18" y="39.5" width="7" height="2.2" rx="1.1" fill="#000" />
                      </g>
                      <!-- 信号波：先描一圈黑边再叠黄线，和游戏里一样带粗黑描边 -->
                      <g stroke-linecap="round">
                        <path d="M34 8.5a7 7 0 0 1 5.5 5.5" stroke="#000" stroke-width="6.5" />
                        <path d="M35.5 2a13.5 13.5 0 0 1 10.5 10.5" stroke="#000" stroke-width="6.5" />
                        <path d="M34 8.5a7 7 0 0 1 5.5 5.5" stroke="#fbfe00" stroke-width="3" />
                        <path d="M35.5 2a13.5 13.5 0 0 1 10.5 10.5" stroke="#fbfe00" stroke-width="3" />
                      </g>
                    </svg>
                  </span>
                  <span class="ik-knock__brand-text">knock knock</span>
                </div>
                <button
                  class="ik-dialog__close"
                  aria-label="关闭"
                  @click="handleClose"
                >
                  <img
                    src="/images/close-btn.webp"
                    alt="关闭"
                    class="ik-dialog__close-img"
                    draggable="false"
                  />
                </button>
              </div>

              <!-- Body：双栏布局 -->
              <div class="ik-dialog__body ik-knock__body">
                <IkZzzMarquee />
                <!-- 左栏：tab + 会话列表 + 活动信息 -->
                <aside class="ik-knock__sidebar">
                  <div class="ik-knock__tabs" role="tablist" aria-label="敲敲分类">
                    <button
                      type="button"
                      role="tab"
                      class="ik-knock__tab"
                      :class="{ 'is-active': activeTab === 'calls' }"
                      :aria-selected="activeTab === 'calls'"
                      aria-label="AI 助手"
                      @click="handleTabClick('calls')"
                    >
                      <PhoneIcon class="ik-knock__tab-icon" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      role="tab"
                      class="ik-knock__tab"
                      :class="{ 'is-active': activeTab === 'contacts' }"
                      :aria-selected="activeTab === 'contacts'"
                      aria-label="私聊"
                      @click="handleTabClick('contacts')"
                    >
                      <UserIcon class="ik-knock__tab-icon" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      role="tab"
                      class="ik-knock__tab"
                      :class="{ 'is-active': activeTab === 'groups' }"
                      :aria-selected="activeTab === 'groups'"
                      aria-label="群聊"
                      @click="handleTabClick('groups')"
                    >
                      <UserGroupIcon class="ik-knock__tab-icon" aria-hidden="true" />
                    </button>
                  </div>

                  <!-- 私聊：原 DM 会话列表 -->
                  <div
                    v-if="activeTab === 'contacts'"
                    ref="contactsListRef"
                    class="ik-knock__list"
                    role="listbox"
                  >
                    <button
                      v-for="item in conversations"
                      :key="item.documentId"
                      type="button"
                      role="option"
                      class="ik-knock__list-item"
                      :class="{
                        'is-active': activeConversationId === item.documentId,
                        'has-unread': item.unreadCount > 0,
                      }"
                      :aria-selected="activeConversationId === item.documentId"
                      @click="handleConversationClick(item.documentId)"
                    >
                      <span class="ik-knock__avatar" aria-hidden="true">
                        <img
                          v-if="item.peer?.avatar || item.avatar"
                          :src="(item.peer?.avatar || item.avatar) as string"
                          :alt="item.peer?.name || item.title || ''"
                          class="ik-knock__avatar-img"
                          draggable="false"
                        />
                        <img v-else src="/images/default-avatar.webp" alt="" class="ik-knock__avatar-img" draggable="false" />
                      </span>
                      <span class="ik-knock__item-text">
                        <span class="ik-knock__item-title">{{ item.peer?.name || item.title || "未知会话" }}</span>
                        <span class="ik-knock__item-subtitle">
                          {{ conversationPreview(item) || "暂无消息" }}
                        </span>
                      </span>
                      <span
                        v-if="item.unreadCount > 0"
                        class="ik-knock__item-badge"
                        aria-label="未读"
                      >
                        {{ item.unreadCount > 99 ? "99+" : item.unreadCount }}
                      </span>
                    </button>
                    <!-- 分页哨兵：进入视口即加载下一页；须常驻 DOM 才能被观察 -->
                    <div
                      v-if="hasMoreConversations && conversations.length"
                      ref="contactsSentinelRef"
                      class="ik-knock__list-more"
                      aria-hidden="true"
                    >
                      <span v-if="isLoadingMoreConversations">加载中…</span>
                    </div>
                    <div
                      v-if="!conversations.length"
                      class="ik-knock__list-empty"
                    >
                      <!-- 整页都被 AI 过滤掉时自动续拉中，别先闪「暂无消息」 -->
                      <span v-if="contactsListLoading || isLoadingMoreConversations">加载中…</span>
                      <span v-else-if="loadError">{{ loadError }}</span>
                      <span v-else>暂无消息</span>
                    </div>
                  </div>

                  <!-- AI 角色（通话 Tab） -->
                  <div
                    v-else-if="activeTab === 'calls'"
                    class="ik-knock__list"
                    role="listbox"
                  >
                    <!-- 角色；选中的角色下面展开它的对话 -->
                    <Collapsible
                      v-for="{ card, unread } in aiCharacterRows"
                      :key="card.slug"
                      :open="activeAiSlug === card.slug"
                      class="ik-knock__ai-group"
                    >
                      <button
                        type="button"
                        role="option"
                        class="ik-knock__list-item"
                        :class="{
                          'is-active': activeAiSlug === card.slug,
                        }"
                        :aria-selected="activeAiSlug === card.slug"
                        :aria-expanded="activeAiSlug === card.slug"
                        @click="selectAiCharacter(card)"
                      >
                        <span class="ik-knock__avatar" aria-hidden="true">
                          <img
                            v-if="cardAvatarUrl(card)"
                            :src="cardAvatarUrl(card)!"
                            :alt="card.displayName"
                            class="ik-knock__avatar-img"
                            draggable="false"
                          />
                          <img v-else src="/images/default-avatar.webp" alt="" class="ik-knock__avatar-img" draggable="false" />
                        </span>
                        <span class="ik-knock__item-text">
                          <span class="ik-knock__item-title">{{ card.displayName }}</span>
                          <span class="ik-knock__item-subtitle">
                            {{ card.bio || "AI 助手" }}
                          </span>
                        </span>
                        <span
                          v-if="unread > 0"
                          class="ik-knock__item-badge"
                          aria-label="未读"
                        >
                          {{ unread > 99 ? "99+" : unread }}
                        </span>
                      </button>
                      <CollapsibleContent class="ik-knock__sessions">
                        <div class="ik-knock__sessions-inner" role="group" :aria-label="`${card.displayName} 的对话`">
                          <button
                            type="button"
                            class="ik-knock__session ik-knock__session--new"
                            :disabled="creatingAiSession"
                            @click="startNewAiSession"
                          >
                            <PlusIcon class="ik-knock__session-new-icon" aria-hidden="true" />
                            <span class="ik-knock__session-title">新对话</span>
                          </button>
                          <div
                            v-for="s in aiSessionsForActiveCard"
                            :key="s.documentId"
                            class="ik-knock__session"
                            :class="{
                              'is-active': activeConversationId === s.documentId,
                              'has-unread': s.unreadCount > 0,
                            }"
                          >
                            <input
                              v-if="renamingSessionId === s.documentId"
                              ref="renameInputRef"
                              v-model="renameDraft"
                              type="text"
                              class="ik-knock__session-input"
                              maxlength="64"
                              :placeholder="aiSessionLabel(s)"
                              aria-label="对话名称"
                              @keydown.enter.prevent="commitRename"
                              @keydown.esc.prevent.stop="cancelRename"
                              @blur="commitRename"
                            />
                            <button
                              v-else
                              type="button"
                              class="ik-knock__session-main"
                              :aria-current="activeConversationId === s.documentId ? 'true' : undefined"
                              @click="openAiSession(s.documentId)"
                              @dblclick="startRename(s)"
                            >
                              <span class="ik-knock__session-title">{{ aiSessionLabel(s) }}</span>
                              <span v-if="s.unreadCount > 0" class="ik-knock__session-dot" aria-label="未读" />
                              <span v-else class="ik-knock__session-time">{{ formatTime(s.lastMessageAt ?? undefined) }}</span>
                            </button>
                            <span v-if="renamingSessionId !== s.documentId" class="ik-knock__session-tools">
                              <button
                                type="button"
                                class="ik-knock__session-tool"
                                aria-label="改名"
                                title="改名"
                                @click="startRename(s)"
                              >
                                <PencilIcon aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                class="ik-knock__session-tool"
                                aria-label="删除"
                                title="删除"
                                :disabled="deletingSessionId === s.documentId"
                                @click="deleteAiSession(s.documentId)"
                              >
                                <TrashIcon aria-hidden="true" />
                              </button>
                            </span>
                          </div>
                          <button
                            v-if="aiSessionsHasMore(card.slug)"
                            type="button"
                            class="ik-knock__session-more"
                            :disabled="aiSessionsLoading(card.slug)"
                            @click="loadAiSessions(card.slug, { more: true })"
                          >
                            {{ aiSessionsLoading(card.slug) ? "加载中…" : "更多" }}
                          </button>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                    <div
                      v-if="!aiCharacters.length"
                      class="ik-knock__list-empty"
                    >
                      <span v-if="aiCharactersLoading">加载中…</span>
                      <span v-else-if="aiCharactersError">{{ aiCharactersError }}</span>
                      <span v-else>暂无 AI 角色</span>
                    </div>
                  </div>

                  <!-- 群聊（占位） -->
                  <div
                    v-else
                    class="ik-knock__list"
                    role="listbox"
                  >
                    <div class="ik-knock__list-empty">
                      <span>暂未开放</span>
                    </div>
                  </div>

                </aside>

                <!-- 右栏：会话标题 + 内容 -->
                <section class="ik-knock__main">
                  <header class="ik-knock__main-header" :class="{ 'has-ai-tools': hasHeaderTools }">
                    <div class="ik-knock__header-identity">
                      <button
                        type="button"
                        class="ik-knock__header-button ik-knock__back"
                        aria-label="返回"
                        @click="handleMobileBack"
                      >
                        <ChevronLeftIcon class="ik-knock__back-icon" aria-hidden="true" />
                      </button>
                      <ChatBubbleLeftIcon class="ik-knock__main-icon" aria-hidden="true" />
                      <div
                        class="ik-knock__main-title-wrap"
                        :class="{ 'is-clickable': canClickPeerProfile }"
                        :role="canClickPeerProfile ? 'button' : undefined"
                        :tabindex="canClickPeerProfile ? 0 : undefined"
                        :aria-label="canClickPeerProfile ? `查看${activeConversation?.peer?.name || '用户'}的主页` : undefined"
                        @click="goToProfile(peerProfileUrl)"
                        @keydown.enter="goToProfile(peerProfileUrl)"
                      >
                        <span class="ik-knock__main-title">
                          {{ activeConversation?.title || activeConversation?.peer?.name || "NoData" }}
                        </span>
                        <Transition name="ik-typing">
                          <span v-if="peerIsTyping" class="ik-knock__typing-indicator" aria-live="polite">
                            <span class="ik-knock__typing-dot" />
                            <span class="ik-knock__typing-dot" />
                            <span class="ik-knock__typing-dot" />
                            <span class="ik-knock__typing-label">正在输入</span>
                          </span>
                        </Transition>
                      </div>
                    </div>
                    <div v-if="hasHeaderTools" class="ik-knock__header-tools">
                      <DmModelPicker
                        :models="activeModelOptions"
                        :model-key="activeModelKey"
                        :default-model-key="activeAiCard?.defaultModelKey ?? null"
                        :streaming="!!activeStreamingMessageId"
                        @select="onSelectModel"
                      />
                      <DmQuotaBar
                        v-if="headerQuota"
                        :percent="headerQuota.percent"
                        :exhausted="headerQuota.exhausted"
                        :reset-at="headerQuota.resetAt"
                      />
                    </div>
                    <div v-if="isActiveAiConversation && activeConversationId" class="ik-knock__header-actions" role="group" aria-label="会话操作">
                      <button
                        type="button"
                        class="ik-knock__header-button ik-knock__session-action"
                        :disabled="deletingSessionId === activeConversationId"
                        aria-label="删除当前会话"
                        title="删除当前会话"
                        @click="deleteAiSession(activeConversationId)"
                      >
                        <TrashIcon class="ik-knock__session-action-icon" aria-hidden="true" />
                      </button>
                    </div>
                  </header>
                  <div class="ik-knock__main-body">
                    <!-- 会话消息流（wrap 承载「回到底部」悬浮按钮的定位上下文） -->
                    <div
                      v-if="activeConversation && activeMessages.length"
                      class="ik-knock__messages-wrap"
                    >
                      <!-- Fairy 专属半透明背景水印（不阻碍消息选择与滚动） -->
                      <FairyMascotEye
                        v-if="isFairyConversation"
                        mode="watermark"
                        :state="fairyMascotState"
                      />
                      <div
                        ref="messagesRef"
                        class="ik-knock__messages"
                        :class="{ 'is-settling': messagesSettling }"
                        tabindex="0"
                        aria-label="消息记录"
                        @scroll.passive="onMessagesScroll"
                      >
                        <div ref="messagesContentRef" class="ik-knock__messages-content">
                          <!-- Phase 4 渲染窗口化：更早消息滚顶自动加载（也可点击） -->
                          <button
                            v-if="hasHiddenAbove"
                            type="button"
                            class="ik-knock__load-earlier"
                            @click="expandRenderWindow"
                          >
                            加载更早的消息
                          </button>
                          <DmMessageItem
                            v-for="entry in visibleMessages"
                            :key="entry.msg.documentId"
                            :entry="entry"
                            :copied-id="copiedMessageId"
                            :show-regenerate="entry.aiRich && entry.msg.documentId === lastAiMessageId && !activeStreamingMessageId"
                            :regenerating="regeneratingAi"
                            @contextmenu="showContextMenu"
                            @profile="goToProfile"
                            @open-post="openPostFromBubble"
                            @bubble-link="handleBubbleLink"
                            @copy="copyMessageText"
                            @regenerate="handleRegenerate"
                            @quote-click="goPost"
                          />
                        </div>
                      </div>
                      <!-- 回到底部 -->
                      <Transition name="ik-b2b">
                        <button
                          v-if="showBackToBottom"
                          type="button"
                          class="ik-knock__back-to-bottom"
                          aria-label="回到底部"
                          @click="handleBackToBottom"
                        >
                          <ChevronDownIcon class="ik-knock__back-to-bottom-icon" aria-hidden="true" />
                        </button>
                      </Transition>
                    </div>
                    <!-- 占位：仅在非加载态时显示，避免切换会话时闪烁 -->
                    <div v-else-if="!activeMessageLoading" class="ik-knock__empty-stage">
                      <div v-if="sendError" class="ik-knock__empty-pill" role="alert">
                        {{ sendError }}
                      </div>
                      <FairyMascotEye
                        v-else-if="isFairyConversation"
                        mode="hero"
                        :state="fairyMascotState"
                        interactive
                      />
                      <div v-else class="ik-knock__empty-pill">
                        EMPTY
                      </div>
                    </div>

                    <!-- 输入框：仅在有选中会话且非匿名/系统会话时显示（Phase 4 拆分为 DmComposer） -->
                    <DmComposer
                      v-if="activeConversation && !composerDisabled"
                      ref="composerRef"
                      v-model:draft="draft"
                      v-model:editing-draft="editingDraft"
                      :disabled="composerDisabled"
                      :placeholder="composerPlaceholder"
                      :rotating-placeholders="composerRotatingPlaceholders"
                      :sending="sending"
                      :editing="!!editingMessageId"
                      :error="sendError"
                      :streaming="!!activeStreamingMessageId"
                      :stopping="stoppingAi"
                      :suggestions="isActiveAiConversation ? activeAiSuggestions : undefined"
                      :suggestions-title="activeAiHasPopular ? '大家都在问' : '试试这样问'"
                      can-attach
                      :attachments="dmImages.tasks.value"
                      @send="doSend"
                      @open-picker="dmImages.openPicker"
                      @attach="dmImages.add"
                      @remove-attachment="dmImages.remove"
                      @retry-attachment="dmImages.retry"
                      @stop="handleStopAi"
                      @cancel-edit="cancelEdit"
                      @typing="handleComposerTyping"
                    />
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- AI 私信附图：与评论同一个图片弹窗（上传新图 / 从我的图库挑），层级高于敲敲 -->
  <Teleport to="body">
    <Transition name="ik-overlay" appear>
      <PostImagePickerModal
        v-if="dmImages.showPicker.value"
        :existing-ids="dmImages.existingIds.value"
        :remaining="dmImages.remaining.value"
        @close="dmImages.showPicker.value = false"
        @upload="dmImages.add"
        @select="dmImages.select"
        @delete="dmImages.removeByServerId($event.documentId)"
      />
    </Transition>
  </Teleport>

  <!-- 消息上下文菜单（编辑 / 撤回）：Teleport 到 body 避免被弹窗剪裁 -->
  <Teleport to="body">
    <div
      v-if="contextMenuMessageId"
      class="ik-knock__context-menu-mask"
      @click="hideContextMenu"
      @contextmenu.prevent="hideContextMenu"
    >
      <div
        class="ik-knock__context-menu"
        :style="contextMenuStyle"
        @click.stop
      >
        <button
          v-if="contextMenuCanCopy"
          type="button"
          class="ik-knock__context-menu-item"
          @click="onContextMenuAction('copy')"
        >
          复制
        </button>
        <button
          v-if="contextMenuCanModify"
          type="button"
          class="ik-knock__context-menu-item"
          @click="onContextMenuAction('edit')"
        >
          编辑
        </button>
        <button
          v-if="contextMenuCanModify"
          type="button"
          class="ik-knock__context-menu-item ik-knock__context-menu-item--danger"
          @click="onContextMenuAction('withdraw')"
        >
          撤回
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* ═══════════════════════════════════════════════
   Overlay 外壳 —— 与委托弹窗 / 登录弹窗完全一致
   ═══════════════════════════════════════════════ */
.ik-overlay {
  position: fixed;
  inset: 0;
  /* 低于委托弹窗 (9000)，保证点击评论委托后委托弹窗叠加在上方 */
  z-index: 8900;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.ik-overlay__stripe {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    40deg,
    transparent,
    transparent 3.5px,
    rgba(255, 255, 255, 0.09) 4.5px,
    rgba(255, 255, 255, 0.09) 7.5px,
    transparent 8.5px
  );
}

/* ── Dialog Shell ──────────────────────────────── */
.ik-dialog {
  position: relative;
}

.ik-dialog--knock {
  width: min(1300px, 86vw);
  height: min(760px, 86vh);
}

.ik-dialog__outer {
  width: 100%;
  height: 100%;
  padding: 4px;
  background: #2d2c2d;
  border-radius: 24px;
  overflow: hidden;
}

.ik-dialog__inner {
  width: 100%;
  height: 100%;
  padding: 4px;
  background: #000;
  border-radius: 22px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Header ────────────────────────────────────── */
.ik-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  flex-shrink: 0;
  border-radius: 18px 18px 0 0;
  background:
    url("/images/tab-bg-point.webp") repeat,
    linear-gradient(180deg, #161616 0%, #080808 100%);
}

.ik-knock__brand {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.ik-knock__brand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.ik-knock__brand-icon > svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* 手机歪着放 */
.ik-knock__brand-phone {
  transform-box: view-box;
  transform-origin: 21px 28px;
  transform: rotate(-14deg);
}

.ik-knock__brand-text {
  font-size: 26px;
  font-weight: 800;
  font-style: normal;
  color: #fff;
  letter-spacing: -0.4px;
  line-height: 1;
}

.ik-dialog__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 140ms ease, transform 140ms ease;
}

.ik-dialog__close:hover {
  opacity: 0.85;
  transform: scale(1.08);
}

.ik-dialog__close:active {
  transform: scale(0.95);
}

.ik-dialog__close-img {
  height: 32px;
  width: auto;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

/* ── Body：左右两栏 ────────────────────────────── */
.ik-dialog__body {
  flex: 1;
  min-height: 0;
  background: #121212;
  border-radius: 0 0 18px 18px;
}

.ik-knock__body {
  display: flex;
  gap: 18px;
  padding: 20px 24px 24px;
  position: relative;
}


/* ── 侧栏 / 主栏 共享面板装饰 ────────────────── */
.ik-knock__sidebar,
.ik-knock__main {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(26, 26, 26, 0.82) 100%
  );
  border-radius: 12px;
  box-shadow: 0 5px 8px rgba(0, 0, 0, 0.85);
}

/* ── Sidebar ──────────────────────────────── */
.ik-knock__sidebar {
  flex: 0 0 292px;
  padding: 14px 12px;
  gap: 14px;
  min-height: 0;
  /* 防止长昵称 / 长文本撑大 sidebar，影响顶部 tabs 对齐 */
  min-width: 0;
}

/* tabs 胶囊：与项目顶部 tab 风格一致；宽度与下方列表项对齐 */
.ik-knock__tabs {
  display: flex;
  align-items: stretch;
  width: 100%;
  padding: 5px;
  gap: 6px;
  border-radius: 999px;
  border: 3px solid #313131;
  background: #050505 url("/images/tab-bg-point.webp") repeat;
}

.ik-knock__tab {
  position: relative;
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 38px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #d9d9d9;
  cursor: pointer;
  transition: background-color 140ms ease, color 140ms ease;
}

.ik-knock__tab:hover {
  color: #fff;
}

.ik-knock__tab.is-active {
  background: #fbfe00;
  color: #000;
}

.ik-knock__tab-icon {
  width: 22px;
  height: 22px;
}

.ik-knock__tab-badge {
  position: absolute;
  top: 2px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #ff3b30;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  border: 1px solid #000;
  pointer-events: none;
}

/* 列表 */
.ik-knock__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* 不预留滚动条轨道，让 list-item 与 tabs 同宽 */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
}

.ik-knock__list::-webkit-scrollbar {
  width: 4px;
}

.ik-knock__list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 2px;
}

.ik-knock__list-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  /* 配合父级 min-width:0，让 ellipsis 真正生效 */
  min-width: 0;
  /* 原 border 4px 补入 padding，保证内容距可见边缘距离不变 */
  padding: 10px 16px;
  border: 0;
  border-radius: 999px;
  /* 内层：棋盘格 chessboard pattern（= <z-pattern type="squares">） */
  background-color: transparent;
  background-image:
    linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.06) 25%,
      transparent 0 75%,
      rgba(255, 255, 255, 0.06) 0
    ),
    linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.06) 25%,
      transparent 0 75%,
      rgba(255, 255, 255, 0.06) 0
    );
  background-position: 0 0, 3px 3px;
  background-size: 6px 6px;
  background-repeat: repeat;
  /* 三层边框全部内缩于元素本身 box 内，确保与 tabs 宽度一致 */
  /* 最外 1px 黑描边 + 内侧 4px 灰描边 */
  box-shadow:
    inset 0 0 0 1px #000,
    inset 0 0 0 5px #3a3a3a;
  color: #888;
  text-align: left;
  cursor: pointer;
  transition: background-color 140ms ease, box-shadow 140ms ease,
    color 140ms ease;
}

.ik-knock__list-item:hover {
  /* 只调底色，保留棋盘纹理；灰描边适度提亮 */
  background-color: rgba(255, 255, 255, 0.04);
  box-shadow:
    inset 0 0 0 1px #000,
    inset 0 0 0 5px rgba(255, 255, 255, 0.35);
}

.ik-knock__list-item.is-active {
  /* 选中态：整块实底主题色，去边框、去 chessboard */
  background-color: #fbfe00;
  background-image: none;
  box-shadow: none;
  color: #000;
}

.ik-knock__list-item.is-active .ik-knock__item-title {
  color: #000;
  font-weight: 800;
}

.ik-knock__list-item.is-active .ik-knock__item-subtitle {
  color: #3a3a3a;
  font-weight: 700;
}

.ik-knock__avatar {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: transparent;
  color: #4a4a4a;
  /* 黑色描边圈：选中/未选中都保留 */
  border: 3px solid #000;
  box-sizing: border-box;
  overflow: hidden;
}

.ik-knock__avatar-icon {
  width: 40px;
  height: 40px;
}

.ik-knock__avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
}

.ik-knock__item-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #fbfe00;
  color: #000;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.ik-knock__list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
}

/* 分页哨兵：兼作「加载中」提示，滚到底时进入视口触发下一页。
   即便没有文案也要占一点高度，否则它会被压成 0px 而无法进入视口。 */
.ik-knock__list-more {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 14px 0;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
}

.ik-knock__item-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.2;
}

.ik-knock__item-title {
  font-size: 16px;
  font-weight: 800;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ik-knock__item-subtitle {
  font-size: 13px;
  font-weight: 700;
  color: #5a5a5a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── 通话 Tab：角色下的对话列表 ─────────────────── */
.ik-knock__ai-group {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.ik-knock__sessions {
  overflow: hidden;
}

.ik-knock__sessions[data-state="open"] {
  animation: ik-sessions-open 200ms ease-out;
}

.ik-knock__sessions[data-state="closed"] {
  animation: ik-sessions-close 160ms ease-in;
}

@keyframes ik-sessions-open {
  from { height: 0; opacity: 0; }
  to { height: var(--reka-collapsible-content-height); opacity: 1; }
}

@keyframes ik-sessions-close {
  from { height: var(--reka-collapsible-content-height); opacity: 1; }
  to { height: 0; opacity: 0; }
}

.ik-knock__sessions-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 8px 0 2px 22px;
  padding-left: 12px;
  border-left: 2px solid #3a3a3a;
}

.ik-knock__session {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 38px;
  border-radius: 10px;
  color: #999;
  transition: background-color 140ms ease, color 140ms ease;
}

.ik-knock__session:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #ddd;
}

.ik-knock__session.is-active,
.ik-knock__session.is-active:hover {
  background: var(--ik-primary, #BFFF09);
  color: #000;
}

.ik-knock__session.is-active::before {
  content: "";
  position: absolute;
  left: -14px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  background: var(--ik-primary, #BFFF09);
}

.ik-knock__session.has-unread:not(.is-active) {
  color: #fff;
}

.ik-knock__session--new {
  gap: 8px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.ik-knock__session--new:disabled {
  opacity: 0.5;
  cursor: default;
}

.ik-knock__session-new-icon {
  flex: none;
  width: 16px;
  height: 16px;
  stroke-width: 2.5;
}

.ik-knock__session-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.ik-knock__session-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ik-knock__session-time {
  flex: none;
  font-size: 12px;
  color: #666;
}

.ik-knock__session.is-active .ik-knock__session-time {
  color: #000;
}

.ik-knock__session-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #fbfe00;
}

.ik-knock__session.is-active .ik-knock__session-dot {
  background: #000;
}

.ik-knock__session-tools {
  display: none;
  flex: none;
  align-items: center;
  gap: 2px;
  padding-right: 6px;
}

.ik-knock__session:hover .ik-knock__session-tools,
.ik-knock__session:focus-within .ik-knock__session-tools {
  display: inline-flex;
}

.ik-knock__session:hover .ik-knock__session-time,
.ik-knock__session:focus-within .ik-knock__session-time {
  display: none;
}

.ik-knock__session-tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.ik-knock__session-tool svg {
  width: 15px;
  height: 15px;
}

.ik-knock__session-tool:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.ik-knock__session.is-active .ik-knock__session-tool:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.1);
  color: #000;
}

.ik-knock__session-tool:disabled {
  opacity: 0.45;
  cursor: default;
}

.ik-knock__session-input {
  flex: 1;
  min-width: 0;
  height: 30px;
  margin: 0 4px;
  padding: 0 8px;
  border: 1px solid #fbfe00;
  border-radius: 8px;
  background: #000;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  outline: none;
}

.ik-knock__session-more {
  height: 32px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #777;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ik-knock__session-more:hover:not(:disabled) {
  color: #ddd;
  background: rgba(255, 255, 255, 0.06);
}

/* 触屏没有悬停：操作按钮常显 */
@media (hover: none) {
  .ik-knock__session-tools {
    display: inline-flex;
  }

  .ik-knock__session-time {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ik-knock__sessions[data-state] {
    animation: none;
  }
}

/* ── Main column ───────────────────────────────── */
.ik-knock__main {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  container-type: inline-size;
}

.ik-knock__main-header {
  --ik-header-control-size: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 72px;
  padding: 12px 18px;
  border-bottom: 3px solid #202020;
}

.ik-knock__header-identity {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 40px;
}

.ik-knock__main-header.has-ai-tools .ik-knock__header-identity {
  flex: 0 1 auto;
  max-width: 220px;
}

.ik-knock__header-tools {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ik-knock__header-actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.ik-knock__header-button {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--ik-header-control-size);
  height: var(--ik-header-control-size);
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #999;
  cursor: pointer;
  transition: color 140ms ease, background-color 140ms ease;
  -webkit-tap-highlight-color: transparent;
}

.ik-knock__header-button:hover:not(:disabled) {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.ik-knock__header-button:focus-visible {
  outline: 2px solid #fbfe00;
  outline-offset: 2px;
}

.ik-knock__header-button:disabled {
  opacity: 0.45;
  cursor: default;
}

.ik-knock__main-icon {
  width: 20px;
  height: 20px;
  color: #454545;
  flex-shrink: 0;
}

.ik-knock__back {
  display: none;
  color: #fff;
}

.ik-knock__back-icon {
  width: 20px;
  height: 20px;
}

.ik-knock__main-title-wrap {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
}

/* 会话标题栏：对端可跳转个人主页时的可点击态 */
.ik-knock__main-title-wrap.is-clickable {
  cursor: pointer;
  border-radius: 6px;
  transition: opacity 140ms ease;
}

.ik-knock__main-title-wrap.is-clickable:hover {
  opacity: 0.8;
}

.ik-knock__main-title-wrap.is-clickable:focus-visible {
  outline: 2px solid #fbfe00;
  outline-offset: 2px;
}

.ik-knock__session-action-icon {
  width: 20px;
  height: 20px;
}

/* system 分界 / 消息气泡 / 引用卡 / 操作条样式随 Phase 4 拆分移至 DmMessageItem.vue */

.ik-knock__main-title {
  font-size: 17px;
  font-weight: 900;
  line-height: 24px;
  color: #fff;
  /* 标题右侧现在挂着模型选择器与额度条：长标题省略，不把它们挤出去 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── 正在输入指示器（header 标题旁；气泡内加载点样式在 DmMessageItem） ── */
.ik-knock__typing-indicator {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 14px;
}

.ik-knock__typing-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  margin-left: 2px;
  letter-spacing: 0.2px;
}

.ik-knock__typing-dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  animation: ik-typing-bounce 1.2s ease-in-out infinite;
}

.ik-knock__typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.ik-knock__typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes ik-typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
  30% { transform: translateY(-4px); opacity: 1; }
}

.ik-typing-enter-active,
.ik-typing-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.ik-typing-enter-from,
.ik-typing-leave-to {
  opacity: 0;
  transform: translateY(2px);
}

.ik-knock__main-body {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 18px;
  overflow: hidden;
}

.ik-knock__empty-stage {
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  padding: 12px;
  overflow: visible;
  position: relative;
}

.ik-knock__empty-pill {
  /* 空态在主区域内居中 */
  margin: auto;
  padding: 16px 88px;
  min-width: 360px;
  text-align: center;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: rgba(255, 255, 255, 0.32);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 6px;
  user-select: none;
}

/* ── 消息流 ────────────────────────────────── */
/* wrap：给「回到底部」悬浮按钮提供定位上下文（1.6） */
.ik-knock__messages-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ik-knock__back-to-bottom {
  position: absolute;
  right: 16px;
  bottom: 12px;
  z-index: 5;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 50%;
  background: #2b2b2e;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  transition: background 140ms ease, color 140ms ease, transform 140ms ease;
}

.ik-knock__back-to-bottom:hover {
  background: #fbfe00;
  color: #000;
  transform: translateY(-2px);
}

.ik-knock__back-to-bottom-icon {
  width: 18px;
  height: 18px;
}

.ik-b2b-enter-active,
.ik-b2b-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.ik-b2b-enter-from,
.ik-b2b-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.ik-knock__messages {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
  overscroll-behavior: contain;
  scroll-behavior: auto;
}

.ik-knock__messages-content {
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 20px;
}

/* 切会话沉降态：消息已渲染但 scrollTop 还没校正到底前先隐藏，
   避免长消息流首帧 paint 在顶部造成"先看到最早消息一闪 → 跳底"抖动 */
.ik-knock__messages.is-settling {
  visibility: hidden;
}

.ik-knock__messages::-webkit-scrollbar {
  width: 4px;
}
.ik-knock__messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 2px;
}

/* Phase 4 渲染窗口化：顶部「加载更早的消息」提示条（滚顶也会自动扩窗） */
.ik-knock__load-earlier {
  align-self: center;
  flex-shrink: 0;
  margin: 2px 0 4px;
  padding: 4px 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}

.ik-knock__load-earlier:hover {
  background: rgba(251, 254, 0, 0.12);
  color: #fbfe00;
}

/* 消息气泡 / 头像 / 时间分隔 / 引用卡 / 操作条 / is-mine 等
   消息级样式随 Phase 4 拆分移至 DmMessageItem.vue（scoped 类名不变） */

/* 入场/出场动画统一在 theme.css 的 .ik-overlay-* 全局规则里维护 */

@container (max-width: 640px) {
  .ik-knock__main-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px 12px;
  }

  .ik-knock__header-identity {
    grid-area: 1 / 1;
  }

  .ik-knock__main-header.has-ai-tools .ik-knock__header-identity {
    max-width: none;
  }

  .ik-knock__header-actions {
    grid-area: 1 / 2;
    margin-left: 0;
  }

  .ik-knock__header-tools {
    grid-area: 2 / 1 / auto / -1;
    justify-content: space-between;
  }
}

/* ── Mobile：QQ / TG 风格全屏单栏（会话列表 ↔ 聊天，二选一） ──
   桌面端是左右双栏；手机端屏幕窄，双栏会挤成「列表 + 空聊天框」割裂体验。
   这里改成原生 IM 模式：默认全屏会话列表，点开某会话后整页切到聊天，
   聊天页顶部用返回箭头回到列表。 */
@media (max-width: 768px) {
  /* 全屏铺满，去掉浮层弹窗外观（圆角 / 边框留白） */
  .ik-dialog--knock {
    width: 100vw;
    height: 100dvh;
    max-width: none;
    max-height: none;
  }

  .ik-dialog--knock .ik-dialog__outer {
    padding: 0;
    border-radius: 0;
    background: #000;
  }

  .ik-dialog--knock .ik-dialog__inner {
    padding: 0;
    border-radius: 0;
  }

  /* 顶部品牌栏（仅列表视图显示）：贴顶 + 顶部安全区 */
  .ik-dialog--knock .ik-dialog__header {
    border-radius: 0;
    padding: 12px 16px;
    padding-top: calc(12px + env(safe-area-inset-top));
  }

  .ik-knock__brand-icon {
    width: 34px;
    height: 34px;
  }

  .ik-knock__brand-text {
    font-size: 20px;
  }

  /* 单栏导航：会话列表常驻底层，聊天页绝对定位覆盖整屏，靠 transform 滑入/滑出 */
  .ik-dialog--knock .ik-dialog__inner {
    position: relative;
  }

  .ik-dialog__body.ik-knock__body {
    display: block;
    position: static;
    padding: 0;
    gap: 0;
    border-radius: 0;
  }

  /* 列表视图：会话列表在正常流中铺满 body（位于品牌栏下方） */
  .ik-knock__sidebar {
    position: relative;
    width: 100%;
    height: 100%;
    flex: none;
    padding: 12px 12px 0;
    gap: 12px;
    border-radius: 0;
    box-shadow: none;
    background: #121212;
  }

  /* 聊天页：绝对定位覆盖整个弹窗（含品牌栏），默认滑出到屏幕右侧外 */
  .ik-knock__main {
    position: absolute;
    inset: 0;
    z-index: 30;
    width: 100%;
    height: 100%;
    flex: none;
    display: flex;
    border-radius: 0;
    box-shadow: none;
    background: #121212;
    transform: translateX(100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
  }

  /* 选中会话：聊天页滑入到位 */
  .ik-dialog--knock.is-mobile-chat .ik-knock__main {
    transform: translateX(0);
  }

  /* tabs：加大触控高度 */
  .ik-knock__tab {
    height: 40px;
  }

  .ik-knock__tab-icon {
    width: 20px;
    height: 20px;
  }

  /* 会话列表项：放大头像与字号，贴近原生 IM 列表 */
  .ik-knock__list {
    gap: 8px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }

  .ik-knock__list-item {
    padding: 12px 16px;
    gap: 14px;
  }

  .ik-knock__avatar {
    width: 50px;
    height: 50px;
  }

  .ik-knock__avatar-icon {
    width: 44px;
    height: 44px;
  }

  .ik-knock__item-title {
    font-size: 16px;
  }

  .ik-knock__item-subtitle {
    font-size: 13px;
  }

  /* 聊天头部：返回箭头 + 对方昵称，贴顶 + 顶部安全区 */
  .ik-knock__back {
    display: inline-flex;
  }

  .ik-knock__main-icon {
    display: none;
  }

  .ik-knock__main-header {
    --ik-header-control-size: 40px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    min-height: 60px;
    padding: 10px 12px;
    padding-top: calc(10px + env(safe-area-inset-top));
    gap: 8px 12px;
    background: linear-gradient(180deg, #161616 0%, #0c0c0c 100%);
    border-bottom: 2px solid #202020;
  }

  .ik-knock__header-identity {
    grid-area: 1 / 1;
    gap: 8px;
  }

  .ik-knock__main-header.has-ai-tools .ik-knock__header-identity {
    max-width: none;
  }

  .ik-knock__header-actions {
    grid-area: 1 / 2;
    margin-left: 0;
  }

  .ik-knock__header-tools {
    grid-area: 2 / 1 / auto / -1;
    justify-content: space-between;
  }

  .ik-knock__main-title {
    font-size: 17px;
  }

  /* 消息区 + 输入框：底部安全区留白，避免被 Home 条遮挡
     （composer 的 safe-area 规则随拆分移至 DmComposer.vue） */
  .ik-knock__main-body {
    padding: 14px;
  }

  .ik-knock__empty-pill {
    padding: 12px 48px;
    min-width: 240px;
    font-size: 16px;
    letter-spacing: 4px;
  }
}

/* is-new 入场动画的 reduced-motion 豁免随拆分移至 DmMessageItem.vue */

/* ═══════════════════════════════════════════════
   DM 私聊：上下文菜单（消息气泡 / Composer 样式已拆分至
   DmMessageItem.vue / DmComposer.vue）
   ═══════════════════════════════════════════════ */

/* ── 上下文菜单（编辑/撤回） ───────────────── */
/* 全屏遮罩：捕获点击关闭菜单 */
.ik-knock__context-menu-mask {
  position: fixed;
  inset: 0;
  /* 高于弹窗主体；与委托弹窗 9000 同级或略高 */
  z-index: 9100;
}

.ik-knock__context-menu {
  position: fixed;
  min-width: 130px;
  padding: 4px;
  background: #1a1a1a;
  border: 2px solid #3a3a3a;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ik-knock__context-menu-item {
  appearance: none;
  border: 0;
  background: transparent;
  color: #e0e0e0;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.ik-knock__context-menu-item:hover,
.ik-knock__context-menu-item:focus-visible {
  background: rgba(251, 254, 0, 0.18);
  color: #fbfe00;
  outline: none;
}

.ik-knock__context-menu-item--danger {
  color: #ff8080;
}

.ik-knock__context-menu-item--danger:hover,
.ik-knock__context-menu-item--danger:focus-visible {
  background: rgba(255, 80, 80, 0.18);
  color: #ff5050;
}

</style>
